//@flow
import * as React from 'react';
import EditableChipsListField from 'common/components/common/EditableChipsListField';
import type { VehicleModelId } from 'common/models/internal/VehicleModelId';
import type { VehicleModel } from 'common/models/internal/Plan';
import type { List } from 'immutable';
import { type ThemeType } from 'react-css-themr';
import { dashboardColors } from 'common/enums/ColorEnum';

type SelectOptionType = {
    label: string,
    value: string
};

type Props = {
    isEditable: boolean,
    emptyStateText: string,
    disableMapEvents: () => void,
    enableMapEvents: () => void,
    vehicleModels: List<VehicleModelId>,
    onVehicleModelsChange: () => void,
    availableVehicleModels: List<VehicleModel>,
    theme: ThemeType,
    showVehicleId: boolean
};

const getVehicleOptionMeta = (option: SelectOptionType): string => {
    return option.value;
};

const getSelectedVehicleModels = (
    vehicleModels: List<VehicleModelId>
): Array<string> => {
    return vehicleModels.map(vehicleModel => vehicleModel.modelId).toArray();
};

const getVehicleOptions = (
    vehicleModels: List<VehicleModel>
): Array<SelectOptionType> => {
    return vehicleModels
        .map(vehicle => {
            const {
                name,
                id: { modelId }
            } = vehicle;
            return { label: name, value: modelId };
        })
        .toArray();
};

export const VehicleModelEditableChips = (props: Props): React.Node => {
    const {
        isEditable,
        emptyStateText,
        disableMapEvents,
        enableMapEvents,
        vehicleModels,
        onVehicleModelsChange,
        availableVehicleModels,
        showVehicleId,
        theme
    } = props;

    const getValueLabel = React.useCallback(
        (value: string): string => {
            const matchedVehicleObj = getVehicleOptions(
                availableVehicleModels
            ).find(option => option.value === value);

            if (!matchedVehicleObj) {
                return value;
            }
            if (showVehicleId) {
                return `${matchedVehicleObj.label} (${matchedVehicleObj.value})`;
            }
            return `${matchedVehicleObj.label}`;
        },
        [availableVehicleModels, showVehicleId]
    );

    const getOptionLabel = React.useCallback(
        (vehicle: SelectOptionType): string => {
            return getValueLabel(vehicle.value);
        },
        [getValueLabel]
    );

    const chipBackgroundHandler = React.useCallback(
        (value: string): ?string => {
            const vehicleFound = !!getVehicleOptions(
                availableVehicleModels
            ).find(option => option.value === value);

            if (!vehicleFound) {
                return dashboardColors.salmon.name;
            }
            return null;
        },
        [availableVehicleModels]
    );

    const options = getVehicleOptions(availableVehicleModels);
    const value = getSelectedVehicleModels(vehicleModels);
    return (
        <EditableChipsListField
            getValueLabel={getValueLabel}
            saveOnEnter={false}
            noHover
            creatable={false}
            emptyStateText={emptyStateText}
            disabled={!isEditable}
            isFocused={isEditable}
            defaultFocused={false}
            onFocus={disableMapEvents}
            onBlur={enableMapEvents}
            onMouseEnter={disableMapEvents}
            onMouseLeave={enableMapEvents}
            theme={theme}
            customFieldClass={vehicleModels.isEmpty() ? theme.isEmpty : null}
            isEditingEnabled={isEditable}
            value={value}
            onSave={onVehicleModelsChange}
            options={options}
            getOptionMeta={getVehicleOptionMeta}
            getOptionLabel={getOptionLabel}
            chipBackground={chipBackgroundHandler}
            doubleLine
        />
    );
};
